import React, { useState, useCallback } from 'react';
import { FaArrowLeft, FaCheckCircle, FaClock, FaExclamationTriangle, FaHourglassHalf } from 'react-icons/fa';
import { getJobDetail, controlGenesysJob, assignusers } from '../api/api';
import { listAssignableUsers } from '../api/users';
import { useParams, useNavigate } from 'react-router-dom';

import { ContactTable } from '../components/ContactTable';
import RefreshButton from '../components/RefreshButton';

const JobDetail = ({ setAlert }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [metadata, setMetadata] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [calls, setCalls] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [callsNextToken, setCallsNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers]   = useState([]);
  const [selectedUsers, setSelectedUsers]     = useState([]);
  const [isLoadingUsers, setIsLoadingUsers]   = useState(false);
  const [isAssigning, setIsAssigning]         = useState(false);

  // Genesys ingestion panel state
  const [genesysProgress, setGenesysProgress] = useState(null);
  const [genesysSubmittedCount, setGenesysSubmittedCount] = useState(0);
  const [controlling, setControlling] = useState(false);
  const [showSamplingAgents, setShowSamplingAgents] = useState(false);

  // Load job details
  const loadJobDetail = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getJobDetail(jobId);
      setMetadata(data.metadata);
      setStatistics(data.statistics);
      setCalls(data.calls || []);
      setTrackers(data.trackers || []);
      setCallsNextToken(data.callsNextToken || null);
      setHasLoaded(true);
      // Seed progress from REST response (supports stopped-and-returned-later)
      if (data.metadata?.genesysProgress) {
        setGenesysProgress(data.metadata.genesysProgress);
      }
      if (data.metadata?.genesysSubmittedCount !== undefined) {
        setGenesysSubmittedCount(data.metadata.genesysSubmittedCount);
      }
    } catch (err) {
      console.error('Error loading job details:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Load more calls
  const loadMoreCalls = useCallback(async () => {
    if (!callsNextToken || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await getJobDetail(jobId, { callsNextToken });
      setCalls(prev => [...prev, ...(data.calls || [])]);
      setCallsNextToken(data.callsNextToken || null);
    } catch (err) {
      console.error('Error loading more calls:', err);
      setAlert({
        type: 'error',
        message: 'Failed to load more calls'
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [jobId, callsNextToken, isLoadingMore, setAlert]);

  // Load job details on mount
  React.useEffect(() => {
    if (!hasLoaded && jobId) {
      loadJobDetail();
    }
  }, [hasLoaded, jobId, loadJobDetail]);

  // Poll every 8 s while ingestion is in flight
  React.useEffect(() => {
    if (metadata?.genesysStatus !== 'RUNNING') return;
    const id = setInterval(loadJobDetail, 30000);
    return () => clearInterval(id);
  }, [metadata?.genesysStatus, loadJobDetail]);

  // Live tracker for non-Genesys jobs (e.g. single/multi-ID uploads): poll while the
  // job is still in progress — calls remaining or files still in the pipeline.
  React.useEffect(() => {
    if (metadata?.genesysStatus) return; // Genesys jobs use the effect above
    if (!statistics) return;
    const inProgress =
      statistics.completedCount < statistics.totalCalls ||
      statistics.processingCount > 0;
    if (!inProgress) return;
    const id = setInterval(loadJobDetail, 8000);
    return () => clearInterval(id);
  }, [metadata?.genesysStatus, statistics, loadJobDetail]);

  // Refresh function
  const mutate = useCallback(() => {
    loadJobDetail();
  }, [loadJobDetail]);

  // Stop or resume the Genesys ingestion Step Function
  const handleControl = async (action) => {
    setControlling(true);
    try {
      await controlGenesysJob(jobId, action);
      if (action === 'stop') {
        setGenesysProgress(p => ({ ...(p || {}), type: 'stopped', message: 'Ingestion stopped by user' }));
        setMetadata(m => ({ ...m, genesysStatus: 'STOPPED' }));
      } else if (action === 'resume') {
        setGenesysProgress(p => ({ ...(p || {}), type: 'progress', phase: 'resuming', message: 'Resuming ingestion…' }));
        setMetadata(m => ({ ...m, genesysStatus: 'RUNNING' }));
      }
    } catch (err) {
      console.error('Control error:', err);
      setAlert({ type: 'error', message: `Failed to ${action} ingestion` });
    } finally {
      setControlling(false);
    }
  };

  const handleAssignClick = async () => {
    setSelectedUsers([]);
    setAssignModalOpen(true);
    setIsLoadingUsers(true);
    try {
      const data = await listAssignableUsers();
      setAvailableUsers(data.users || data.Users || []);
    } catch (err) {
      console.error('Failed to load users', err);
      setAlert({ type: 'error', message: 'Failed to load assignable users' });
      setAssignModalOpen(false);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const toggleUser = (user) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.email === user.email);
      return exists ? prev.filter(u => u.email !== user.email) : [...prev, { email: user.email, name: user.username }];
    });
  };

  const handleAssignSubmit = async () => {
    setIsAssigning(true);
    try {
      await assignusers({ jobId, agents: selectedUsers });
      setAssignModalOpen(false);
      setAlert({ type: 'success', message: `Job assigned to ${selectedUsers.length} agent(s)` });
    } catch (err) {
      console.error('Assignment failed', err);
      setAlert({ type: 'error', message: 'Failed to assign job' });
    } finally {
      setIsAssigning(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/jobs')}
          className="btn-outline flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Jobs
        </button>
        <div className="card bg-red-50">
          <p className="text-red-700">Failed to load job details. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/jobs')}
          className="btn-outline flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Jobs
        </button>
        <div className="card bg-yellow-50">
          <p className="text-yellow-700">Job not found.</p>
        </div>
      </div>
    );
  }

  const exportResults = (format) => {
    if (callsNextToken) {
      const confirmExport = window.confirm(
        'Are you sure you want to export without loading all calls?'
      );

      if (!confirmExport) {
        return;
      }
    }
    if (format === 'csv') {
      const headers = ['Timestamp', 'Job Name', 'Agent', 'Campaign', 'Template', 'QA Score', 'Compliance', 'Duration', 'GUID', 'Conversation ID', 'DNIS'];
      const rows = calls.map(item => [
        new Date(item.timestamp).toISOString(),
        item.jobName || '',
        item.agent || '',
        item.customer || '',
        item.template || '',
        item.Result?.QA_Score ?? 'N/A',
        item.Result?.Compliance ? 'Pass' : 'Fail',
        item.duration || '',
        item.guid || '',
        item.conversationId || '',
        item.dnis || ''
      ]);
      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(calls, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const TRACKER_STATUS_CONFIG = {
    queued:        { label: 'Queued',        bg: '#f3f4f6', color: '#374151', pulse: true  },
    transcribing:  { label: 'Transcribing',  bg: '#dbeafe', color: '#1d4ed8', pulse: true  },
    processing:    { label: 'Processing',    bg: '#fef3c7', color: '#b45309', pulse: true  },
    failed:        { label: 'Failed',        bg: '#fee2e2', color: '#b91c1c', pulse: false },
    quota_stopped: { label: 'Quota Stopped', bg: '#fef3c7', color: '#92400e', pulse: false },
  };

  const TrackerStatusBadge = ({ status }) => {
    const cfg = TRACKER_STATUS_CONFIG[status] || TRACKER_STATUS_CONFIG.queued;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium`}
        style={{ backgroundColor: cfg.bg, color: cfg.color }}
      >
        {cfg.pulse && (
          <span
            className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: cfg.color }}
          />
        )}
        {cfg.label}
      </span>
    );
  };

  const genesysStatus = metadata?.genesysStatus;
  const isLimitReached = genesysStatus === 'QUOTA_LIMIT';
  const isStopped = genesysStatus === 'STOPPED' || genesysProgress?.type === 'stopped';
  const isComplete = genesysStatus === 'COMPLETE' || genesysProgress?.type === 'complete';
  const isRunning = genesysStatus === 'RUNNING' && !isComplete && !isLimitReached;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/jobs')}
            className="btn-outline flex items-center"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Details</h1>
            <p className="text-gray-600 mt-1">
              {jobId === 'legacy' ? 'Legacy Job' : `Job ID: ${jobId}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={handleAssignClick} className="btn-primary">
            Assign Job
          </button>
          <RefreshButton mutate={mutate} />
        </div>
      </div>

      {/* Job Metadata */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Upload Date</p>
            <p className="text-base font-medium text-gray-900">
              {formatDate(metadata.uploadDate)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Campaign</p>
            <p className="text-base font-medium text-gray-900">{metadata.campaign}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Template</p>
            <p className="text-base font-medium text-gray-900">{metadata.template}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Uploaded By</p>
            <p className="text-base font-medium text-gray-900">{metadata.uploadedBy}</p>
          </div>
        </div>
      </div>

      {/* PCA quota limit banner — shown when non-Genesys calls were stopped by quota */}
      {metadata.quotaStoppedCount > 0 && !genesysStatus && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Daily transcription limit reached</p>
            <p className="text-sm text-amber-700 mt-1">
              {metadata.quotaStoppedCount} call{metadata.quotaStoppedCount !== 1 ? 's' : ''} could not
              be transcribed because the daily Transcribe quota was exhausted. Contact a SuperAdmin to
              increase the limit and resume these calls via the Transcribe Quota page.
            </p>
          </div>
        </div>
      )}

      {/* Genesys quota limit card — replaces ingestion panel when QUOTA_LIMIT */}
      {isLimitReached && (
        <div className="card border-l-4 border-amber-400">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0 text-xl" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Daily Transcription Limit Reached</h2>
              <p className="text-sm text-gray-600 mt-1">
                This job was paused because the daily Transcribe quota was exhausted.
                No further recordings will be processed until the limit is increased.
              </p>
              <p className="text-sm font-medium text-amber-700 mt-2">
                Contact a SuperAdmin to increase the daily limit and resume processing
                via the Transcribe Quota page.
              </p>
              {metadata.quotaStoppedCount > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {metadata.quotaStoppedCount} execution{metadata.quotaStoppedCount !== 1 ? 's' : ''} paused by quota
                </p>
              )}
            </div>
          </div>
          <div className="mt-3">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Quota limit reached
            </span>
          </div>
        </div>
      )}

      {/* Genesys Ingestion Panel */}
      {(genesysProgress || genesysStatus) && !isLimitReached && (
        <div className="card border-l-4 border-cyan-500">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Genesys Ingestion Workflow</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Recordings are submitted in batches of up to 100. Runs independently of QA scoring.
              </p>
            </div>
            <div>
              {isStopped && !isComplete && (
                <button
                  onClick={() => handleControl('resume')}
                  disabled={controlling}
                  className="btn-primary"
                >
                  {controlling ? 'Starting…' : 'Resume Ingestion'}
                </button>
              )}
              {isRunning && !isStopped && (
                <button
                  onClick={() => handleControl('stop')}
                  disabled={controlling}
                  className="btn-outline"
                  style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                >
                  {controlling ? 'Stopping…' : 'Stop Ingestion'}
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="h-3 rounded-full transition-all duration-500"
              style={{
                width: `${genesysProgress?.percentage || 0}%`,
                backgroundColor: isStopped ? '#6b7280' : isComplete ? '#22c55e' : '#0891b2'
              }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600 mb-4">
            <span>{genesysProgress?.message || 'Waiting for first batch…'}</span>
            <span className="font-medium">{genesysProgress?.percentage || 0}%</span>
          </div>

          {/* Batch stats */}
          {genesysProgress?.totalChunks > 0 && (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-gray-50 rounded p-2">
                <p className="text-gray-500 text-xs">Batch</p>
                <p className="font-semibold">
                  {genesysProgress.currentChunk} / {genesysProgress.totalChunks}
                </p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-gray-500 text-xs">Recordings</p>
                <p className="font-semibold">
                  {genesysSubmittedCount.toLocaleString()} / ~{(genesysProgress.approxTotal || genesysProgress.totalChunks * 100).toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-gray-500 text-xs">Per batch</p>
                <p className="font-semibold">≤ 100</p>
              </div>
            </div>
          )}

          <div className="mt-3">
            <span
              className="px-2 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: isStopped ? '#f3f4f6' : isComplete ? '#dcfce7' : '#cffafe',
                color: isStopped ? '#374151' : isComplete ? '#15803d' : '#0e7490'
              }}
            >
              {isStopped
                ? 'Ingestion stopped'
                : isComplete
                ? 'Ingestion complete'
                : genesysProgress?.phase?.replace(/_/g, ' ') || 'Running'}
            </span>
          </div>

          {/* Per-agent sampling summary */}
          {metadata?.samplingSummary && (
            <div className="mt-4 border-t pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Per-Agent Sampling</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {metadata.samplingSummary.agentsFound} agent{metadata.samplingSummary.agentsFound === 1 ? '' : 's'} found
                    {' · '}{metadata.samplingSummary.callsPerAgent} call{metadata.samplingSummary.callsPerAgent === 1 ? '' : 's'}/agent ({metadata.samplingSummary.scope})
                    {' · '}sampled {metadata.samplingSummary.sampledConversations?.toLocaleString()} of {metadata.samplingSummary.totalConversations?.toLocaleString()} calls
                  </p>
                </div>
                {metadata.samplingSummary.agents?.length > 0 && (
                  <button
                    onClick={() => setShowSamplingAgents(v => !v)}
                    className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    {showSamplingAgents ? '▲ Hide agents' : '▼ Show agents'}
                  </button>
                )}
              </div>

              {showSamplingAgents && metadata.samplingSummary.agents?.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">Agent</th>
                        <th className="text-right font-medium px-3 py-2">Sampled</th>
                        <th className="text-right font-medium px-3 py-2">Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadata.samplingSummary.agents.map((a) => {
                        const underTarget = a.available < metadata.samplingSummary.callsPerAgent;
                        return (
                          <tr key={a.agentId} className="border-t">
                            <td className="px-3 py-1.5 text-gray-800">{a.agentName}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{a.sampled}</td>
                            <td className={`px-3 py-1.5 text-right ${underTarget ? 'text-amber-600' : 'text-gray-500'}`}>
                              {a.available}{underTarget ? ' ⚠' : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Calls</p>
              {genesysStatus ? (
                <p className="text-2xl font-bold text-gray-900">
                  {genesysSubmittedCount.toLocaleString()}
                  {genesysProgress?.approxTotal > 0 && (
                    <span className="text-base font-normal text-gray-500"> / ~{statistics.totalCalls.toLocaleString()}</span>
                  )}
                </p>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{statistics.totalCalls}</p>
              )}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{statistics.completedCount}</p>
            </div>
            <FaCheckCircle className="text-3xl text-green-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Processing</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.processingCount}</p>
            </div>
            <FaClock className="text-3xl text-blue-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{statistics.failedCount}</p>
            </div>
            <FaExclamationTriangle className="text-3xl text-red-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completion</p>
              <p className="text-2xl font-bold text-cyan-600">{statistics.completionPercentage}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Average Score */}
      {statistics.averageScore !== null && (
        <div className="card bg-gradient-to-r from-cyan-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average QA Score</p>
              <p className="text-3xl font-bold text-cyan-600">{statistics.averageScore}</p>
            </div>
          </div>
        </div>
      )}

      {/* In-Progress Files */}
      {trackers.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <FaHourglassHalf className="text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Files In Progress
            </h2>
            <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              {trackers.length}
            </span>
          </div>
          {trackers.some(t => t.status === 'quota_stopped') && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <FaExclamationTriangle className="mt-0.5 flex-shrink-0 text-amber-500" />
              <span>
                Some files were stopped because the daily Transcribe quota was reached.
                Visit the <strong>Transcribe Quota</strong> page to increase the limit and resume them.
              </span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Status', 'File', 'Agent', 'Campaign', 'Template', 'Queued At'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {trackers.map((t, i) => (
                  <tr key={t.filename || i} className="bg-white hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <TrackerStatusBadge status={t.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono max-w-xs truncate" title={t.filename}>
                      {t.filename}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {t.agent || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {t.campaign || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {t.template || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {t.timestamp ? new Date(t.timestamp).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Calls Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Calls in this Job</h2>
        <ContactTable
          data={calls}
          setAlert={setAlert}
          showJobId={false}
          header={
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Search Results ({calls.length})
              </h2>
              <div className="flex space-x-2" style={{paddingLeft: '10px'}}>
                <button
                  onClick={() => exportResults('csv')}
                  className="btn-outline"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => exportResults('json')}
                  className="btn-outline"
                >
                  Export JSON
                </button>
              </div>
            </div>
          }
        />

        {/* Load More Calls Button */}
        {callsNextToken && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
            <button
              onClick={loadMoreCalls}
              disabled={isLoadingMore}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                'Load More Calls'
              )}
            </button>
          </div>
        )}
      </div>
      {/* Assign Job Modal */}
      {assignModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Assign Job</h2>
            <p className="text-sm text-gray-500 mb-4">
              {jobId === 'legacy' ? 'Legacy Job' : `Job ID: ${jobId}`}
            </p>

            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                <span className="ml-3 text-gray-600">Loading agents...</span>
              </div>
            ) : availableUsers.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">No assignable agents found.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded mb-4">
                {availableUsers.map(user => (
                  <label
                    key={user.email || user.username}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 mr-3"
                      checked={selectedUsers.some(u => u.email === user.email)}
                      onChange={() => toggleUser(user)}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.username}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-2">
              <button
                onClick={() => setAssignModalOpen(false)}
                className="btn-outline"
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={selectedUsers.length === 0 || isAssigning || isLoadingUsers}
              >
                {isAssigning ? 'Assigning...' : `Assign (${selectedUsers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
